import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { getFilmById, updateFilm, createFilm, getFornitori, deleteFilmImage, generateFilmWebDescription, searchTmdbFilms, updateFilmFromTmdb, getGeneriPersonalizzati, getCaseDiProduzione, getUbicazioni, scrapeIbsUrl, scrapeDvdStore, getCompetitivePrice, generateEditorialBarcode, getTipiUscita, updateUbicazioniFilm, getImpostazioniPrezzi, syncFilmMirakl } from '../services/api';
import { Form, Button, Container, Row, Col, Card, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';
import { calcolaCostoAcquisto } from '../utils/costoAcquisto';
import { applicaRiedizioneDaAnno, isRiedizioneDaAnno } from '../utils/riedizione';

function formatErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
  if (detail) return JSON.stringify(detail);
  return err.message;
}

const FilmEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tornaIndietro = useTornaIndietro('/film');
  const location = useLocation();
  const [film, setFilm] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [suggerimenti, setSuggerimenti] = useState({ generi: [], caseDiProduzione: [], ubicazioni: [], tipiUscita: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [tmdbResults, setTmdbResults] = useState([]);
  const [showTmdbResults, setShowTmdbResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [ibsUrl, setIbsUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [isCalcoloPrezzo, setIsCalcoloPrezzo] = useState(false);
  const [isGeneratingBarcode, setIsGeneratingBarcode] = useState(false);
  const [isDvdStoreScraping, setIsDvdStoreScraping] = useState(false);
  const [ubicazioniRows, setUbicazioniRows] = useState([]); // [{ ubicazione, quantita }]
  const [saving, setSaving] = useState(false);
  const [percentualeIbs, setPercentualeIbs] = useState(0);
  const [markupFissoIbs, setMarkupFissoIbs] = useState(0);
  // Finché è true il costo acquisto segue le regole SKU; si disattiva quando l'utente lo scrive a mano
  const [costoAuto, setCostoAuto] = useState(true);

  const isCreating = !id;

  useEffect(() => {
    getImpostazioniPrezzi()
      .then(res => {
        setPercentualeIbs(res.data.ibs_markup_percentuale || 0);
        setMarkupFissoIbs(res.data.ibs_markup_fisso || 0);
      })
      .catch(() => {});
  }, []);

  const calcolaPrezzoIbs = (prezzoNegozio) => {
    if (!prezzoNegozio) return null;
    return arrotondaANovanta(prezzoNegozio * (1 + (parseFloat(percentualeIbs) || 0) / 100) + (parseFloat(markupFissoIbs) || 0));
  };

  // Stesso arrotondamento psicologico dell'equivalente backend (arrotonda_a_novanta).
  const arrotondaANovanta = (prezzo) => {
    if (!prezzo || prezzo <= 0) return prezzo;
    return Math.round((Math.floor(prezzo - 0.5) + 0.9) * 100) / 100;
  };

  const debouncedSearch = React.useRef(
    debounce(async (query) => {
        if (query.length > 2) {
            try {
                const response = await searchTmdbFilms(query);
                setTmdbResults(response.data);
                setShowTmdbResults(true);
            } catch (error) {
                console.error("Errore nella ricerca TMDB:", error);
                setError("Errore durante la ricerca su TMDB.");
            }
        } else {
            setShowTmdbResults(false);
        }
    }, 500)
  ).current;

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);


  // Sposta la definizione di fetchData fuori da useEffect e usa useCallback
  const fetchData = useCallback(async () => {
    try {
      const [fornitoriRes, generiRes, caseProdRes, ubicazioniRes, tipiUscitaRes] = await Promise.all([
        getFornitori(0, 5000),
        getGeneriPersonalizzati(),
        getCaseDiProduzione(),
        getUbicazioni(),
        getTipiUscita()
      ]);
      setFornitori(fornitoriRes.data.fornitori);
      setSuggerimenti({ generi: generiRes.data, caseDiProduzione: caseProdRes.data, ubicazioni: ubicazioniRes.data, tipiUscita: tipiUscitaRes.data });

      if (isCreating) {
        // Modalità Creazione
        setFilm({
          titolo: '',
          anno: '',
          genere: '',
          regista: '',
          attori: '',
          trama: '',
          durata: '',
          rating: '',
          casa_di_produzione: '',
          casa_di_distribuzione: '',
          categoria: 'DVD',
          supporto: '',
          quantita: 1,
          sku: '',
          barcode: location.state?.barcode || '',
          barcode_ibs: '',
          prezzo_negozio: '',
          prezzo_ibs: '',
          costo_acquisto: '',
          iva: 22.0,
          versione_noleggio: false,
          versione_editoriale: false,
          fuori_catalogo: false,
          da_pubblicare_su_ibs: true,
          in_vetrina: false,
          prenotabile: false,
          data_uscita: '',
          riedizione: false,
          tipo_uscita: '',
          fornitore_id: null,
          ubicazione: '',
          descrizione_web: '',
          note_ibs: ''
        });
        setUbicazioniRows([]);
      } else {
        // Modalità Modifica
        const filmRes = await getFilmById(id);
        setFilm(filmRes.data);
        // Un film già salvato con un costo non va ricalcolato finché non si cambia lo SKU
        setCostoAuto(!filmRes.data.costo_acquisto);
        setUbicazioniRows((filmRes.data.ubicazioni_stock || []).map(r => ({ ubicazione: r.ubicazione, quantita: r.quantita })));
      }

      setLoading(false);
    } catch (err) {
      setError('Errore nel caricamento dei dati');
      setLoading(false);
    }
  }, [id, isCreating, location.state]); // Aggiunte dipendenze

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Solo quando l'anno cambia dopo il caricamento: aprire un film esistente non ne modifica Riedizione
  const annoPrecedente = React.useRef(undefined);
  useEffect(() => {
    if (!film) return;
    const primoCaricamento = annoPrecedente.current === undefined;
    const cambiato = annoPrecedente.current !== film.anno;
    annoPrecedente.current = film.anno;
    if (!cambiato || (primoCaricamento && !isCreating)) return;
    if (isRiedizioneDaAnno(film.anno) && !(film.riedizione && film.tipo_uscita)) {
      setFilm(prev => applicaRiedizioneDaAnno(prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [film?.anno]);

  useEffect(() => {
    if (!film || !costoAuto) return;
    const costo = calcolaCostoAcquisto(film);
    if (costo !== null && parseFloat(film.costo_acquisto) !== costo) {
      setFilm(prev => ({ ...prev, costo_acquisto: costo }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [film?.sku, film?.supporto, film?.prezzo_negozio_base, film?.prezzo_negozio, costoAuto]);

  useEffect(() => {
    if (loading || !film || !location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = target.querySelector('input, select, textarea');
      if (input) input.focus();
    }
  }, [loading, film, location.hash]);

  useEffect(() => {
    // This effect manages the image preview URL based on the state of `film` and `selectedImage`.
    
    // Priority 1: A new local image has been selected for upload. Show a preview of it.
    if (selectedImage) {
      setImagePreviewUrl(URL.createObjectURL(selectedImage));
      return; // Exit early
    }

    // Priority 2: The film data from the server has an image URL.
    if (film && film.immagine_url) {
      const url = film.immagine_url;
      // If the URL is absolute (starts with http), use it directly.
      // Otherwise, prepend the API base URL to create the full path.
      setImagePreviewUrl(url.startsWith('http') ? url : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${url}`);
    } else {
      // If there's no selected local image and no image URL from the server, show no preview.
      setImagePreviewUrl(null);
    }
  }, [film, selectedImage]); // Re-run this logic whenever the film data or the selected image changes.




  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;
    if (type === 'checkbox') {
        finalValue = checked;
    } else if (type === 'number') {
        finalValue = value === '' ? null : parseFloat(value);
    }

    setFilm(prev => {
      const updated = { ...prev, [name]: finalValue };
      if (name === 'prezzo_negozio') {
        updated.prezzo_ibs = calcolaPrezzoIbs(prev.prezzo_negozio_base || finalValue);
      }
      return updated;
    });
  };

  const quantitaTotale = ubicazioniRows.reduce((sum, r) => sum + (parseInt(r.quantita) || 0), 0);

  const handleUbicazioneRowChange = (index, field, value) => {
    setUbicazioniRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: field === 'quantita' ? (parseInt(value) || 0) : value } : r));
  };

  const handleAddUbicazioneRow = () => {
    setUbicazioniRows(prev => [...prev, { ubicazione: '', quantita: 0 }]);
  };

  const handleRemoveUbicazioneRow = (index) => {
    setUbicazioniRows(prev => prev.filter((_, i) => i !== index));
  };

  // Logica SKU spostata nel backend
  const handleCostoChange = (e) => {
    setCostoAuto(e.target.value === '');
    handleChange(e);
  };

  const handleSkuChange = (e) => {
    const { value } = e.target;
    setCostoAuto(true);
    setFilm(prev => ({
      ...prev,
      sku: value.toUpperCase(),
    }));
  };

  const handleGeneraBarcode = async () => {
    setIsGeneratingBarcode(true);
    try {
      const res = await generateEditorialBarcode();
      const { barcode, sku_nuovo, sku_usato } = res.data;
      const condizione = film.condizione || 'Nuovo';
      const isNuovo = condizione === 'Nuovo';
      const suggeritedSku = isNuovo ? sku_nuovo : sku_usato;
      const nuovaCondizione = isNuovo ? 'Nuovo' : 'Usato - Ottime Condizioni';
      setFilm(prev => ({
        ...prev,
        barcode,
        sku: suggeritedSku,
        condizione: nuovaCondizione,
      }));
      toast.success(`Barcode editoriale generato: ${barcode}`);
    } catch (err) {
      toast.error('Errore nella generazione del barcode editoriale');
    } finally {
      setIsGeneratingBarcode(false);
    }
  };

  const handleListBlur = (e) => {
    const { name, value } = e.target;
    const formattedValue = value.replace(/,\s*/g, '; ');
    setFilm(prev => ({ ...prev, [name]: formattedValue }));
  };

  const DVD_STORE_FIELDS = ['titolo', 'genere', 'attori', 'regista', 'anno', 'durata', 'trama', 'casa_di_produzione', 'casa_di_distribuzione', 'supporto', 'prezzo_dvdstore', 'tipo_uscita', 'riedizione', 'immagine_url'];

  const handleBarcodeIbsBlur = async (e) => {
    const ean = e.target.value.trim();
    if (ean) {
      // Di default il barcode IBS diventa barcode e SKU MC01_<barcode>, senza toccare valori già presenti
      setFilm(prev => ({
        ...prev,
        barcode: prev.barcode || ean,
        sku: prev.sku || `MC01_${ean}`,
        condizione: !prev.sku && !prev.condizione ? 'Nuovo' : prev.condizione,
      }));
      if (!film.sku) setCostoAuto(true);
    }
    if (!/^\d{8,13}$/.test(ean)) return;
    setIsDvdStoreScraping(true);
    try {
      const res = await scrapeDvdStore(ean);
      const d = res.data;
      // Blur: popola solo campi vuoti
      setFilm(prev => {
        const updated = { ...prev };
        DVD_STORE_FIELDS.forEach(f => { if (d[f] != null && !prev[f]) updated[f] = d[f]; });
        if (d.fuori_catalogo_dvdstore) updated.fuori_catalogo = true;
        return updated;
      });
      toast.success(`DVD Store: trovato "${d.titolo || ean}"${d.fuori_catalogo_dvdstore ? ' — Fuori catalogo' : ''}`);
    } catch (err) {
      if (err?.response?.status !== 404) toast.error('Errore scraping DVD Store');
    } finally {
      setIsDvdStoreScraping(false);
    }
  };

  const handleImportDvdStore = async () => {
    const ean = film?.barcode_ibs?.trim();
    if (!ean || !/^\d{8,13}$/.test(ean)) {
      toast.error('Inserisci prima un Barcode IBS valido (8-13 cifre)');
      return;
    }
    setIsDvdStoreScraping(true);
    try {
      const res = await scrapeDvdStore(ean);
      const d = res.data;
      // Pulsante: sovrascrive tutti i campi trovati
      setFilm(prev => {
        const updated = { ...prev };
        DVD_STORE_FIELDS.forEach(f => { if (d[f] != null) updated[f] = d[f]; });
        if (d.fuori_catalogo_dvdstore) updated.fuori_catalogo = true;
        return updated;
      });
      toast.success(`DVD Store: importato "${d.titolo || ean}"${d.fuori_catalogo_dvdstore ? ' — Fuori catalogo' : ''}`);
    } catch (err) {
      if (err?.response?.status === 404) toast('Film non trovato su DVD Store', { icon: '⚠️' });
      else toast.error('Errore scraping DVD Store');
    } finally {
      setIsDvdStoreScraping(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedImage(file);
  };

  const handleDeleteImage = async () => {
    if (window.confirm('Sei sicuro di voler eliminare l\'immagine?')) {
      try {
        await deleteFilmImage(id);
        setFilm(prevFilm => ({ ...prevFilm, immagine_url: null }));
        setSelectedImage(null);
        setImagePreviewUrl(null);
        toast.success('Immagine eliminata con successo!');
      } catch (err) {
        console.error("Errore nell'eliminazione dell'immagine:", err);
        toast.error("Errore nell'eliminazione dell'immagine.");
      }
    }
  };

  const handleCalcolaPrezzo = async () => {
    const ean = film?.barcode_ibs;
    const condizione = film?.condizione || 'Nuovo';
    if (!ean) {
      toast.error('Inserisci prima il Barcode IBS (EAN)');
      return;
    }
    setIsCalcoloPrezzo(true);
    try {
      const res = await getCompetitivePrice(ean, condizione, film?.costo_acquisto);
      const { competitive_price, found } = res.data;
      if (found && competitive_price !== null) {
        setFilm(prev => ({ ...prev, prezzo_ibs: competitive_price }));
        toast.success(`Prezzo IBS €${competitive_price.toFixed(2)}: batte ${res.data.concorrente} (${res.data.mercato_totale?.toFixed(2)}€ consegnato)${res.data.battibile === false ? ' — impostato il minimo per il margine' : ''}`);
      } else {
        toast('Nessun prezzo competitor trovato per questo EAN/condizione', { icon: '⚠️' });
      }
    } catch (err) {
      toast.error('Errore nel calcolo del prezzo: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setIsCalcoloPrezzo(false);
    }
  };


  const [sincronizzandoMirakl, setSincronizzandoMirakl] = useState(false);

  const handleRisincronizzaMirakl = async () => {
    setSincronizzandoMirakl(true);
    try {
      const res = await syncFilmMirakl(id);
      if (res.data.ok) toast.success(res.data.messaggio); else toast.warning(res.data.messaggio, { autoClose: false });
    } catch (err) {
      toast.error('Risincronizzazione Mirakl non riuscita: ' + formatErrorDetail(err));
    } finally {
      setSincronizzandoMirakl(false);
    }
  };

  const notificaSalvataggio = (titolo, messaggio) => {
    const dettaglio = (messaggio || '').trim();
    if (dettaglio.includes('ATTENZIONE')) {
      toast.warning(`${titolo} ${dettaglio}`, { autoClose: false });
    } else {
      toast.success(dettaglio ? `${titolo} ${dettaglio}` : titolo);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const filmDataForJson = { ...film };
      delete filmDataForJson.fornitore; // Rimuovi oggetti annidati prima dello stringify
      // Quantità e ubicazione sono derivate dalle righe ubicazioni_stock, salvate a parte
      delete filmDataForJson.quantita;
      delete filmDataForJson.ubicazione;

      const formData = new FormData();
      formData.append('film_data', JSON.stringify(filmDataForJson));

      if (selectedImage) {
        formData.append('immagine_file', selectedImage);
      }

      let response;
      if (isCreating) {
        response = await createFilm(formData);
        notificaSalvataggio('Film creato con successo!', response.data.image_message);
      } else {
        response = await updateFilm(id, formData);
        notificaSalvataggio('Film aggiornato con successo!', response.data.image_message);
      }

      const filmId = response.data.film.id;
      const righeValide = ubicazioniRows
        .map(r => ({ ubicazione: (r.ubicazione || '').trim(), quantita: parseInt(r.quantita) || 0 }))
        .filter(r => r.ubicazione && r.quantita > 0);
      const ubicazioniRes = await updateUbicazioniFilm(filmId, righeValide);
      response.data.film.ubicazioni_stock = ubicazioniRes.data;
      response.data.film.quantita = ubicazioniRes.data.reduce((sum, r) => sum + r.quantita, 0);

            setFilm(response.data.film);
            setUbicazioniRows(ubicazioniRes.data.map(r => ({ ubicazione: r.ubicazione, quantita: r.quantita })));
      
      
      
            if (location.state?.from === 'inventory') {
      
              navigate('/magazzino/inventario', { state: { shouldRefresh: true } });
      
            } else {
      
              navigate('/film', { state: { shouldRefresh: true } });
      
            }
      
            
      
          } catch (error) {
      console.error("Errore nel salvataggio del film:", error);
      const errorMessage = error.response?.data?.detail ? JSON.stringify(error.response.data.detail) : "Errore durante il salvataggio.";
      setError(errorMessage);
      setSaving(false);
    }
  };

  const handleGenerateWebDescription = async () => {
    if (isCreating) {
        toast.warning("Salva prima il film per poter generare una descrizione.");
        return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await generateFilmWebDescription(id);
      setFilm(response.data);
      toast.success('Descrizione web generata con successo!');
    } catch (err) {
      setError('Errore durante la generazione della descrizione web.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportTMDB = async (tmdbId) => {
    if (isCreating) {
        toast.warning("Salva prima il film per poter importare da TMDB.");
        return;
    }
    setIsImporting(true);
    setError('');
    try {
        const response = await updateFilmFromTmdb(id, tmdbId); // Ora restituisce FilmCreateUpdateResponse
        const importedFilm = response.data.film;
        const imageMessage = response.data.image_message;

        setFilm(prev => ({
            ...prev,
            titolo: importedFilm.titolo ?? prev.titolo,
            anno: importedFilm.anno ?? prev.anno,
            genere: importedFilm.genere ?? prev.genere,
            regista: importedFilm.regista ?? prev.regista,
            attori: importedFilm.attori ?? prev.attori,
            trama: importedFilm.trama ?? prev.trama,
            casa_di_produzione: importedFilm.casa_di_produzione ?? prev.casa_di_produzione,
            tmdb_id: importedFilm.tmdb_id ?? prev.tmdb_id,
            durata: importedFilm.durata ?? prev.durata,
            rating: importedFilm.rating ?? prev.rating,
            immagine_url: importedFilm.immagine_url ?? prev.immagine_url,
        }));
        alert(`Dati importati da TMDB con successo! ${imageMessage}`);
        setShowTmdbResults(false);
    } catch (error) {
        alert(error.response?.data?.detail || "Errore durante l'importazione da TMDB.");
        console.error("Errore importazione TMDB:", error);
    } finally {
        setIsImporting(false);
    }
  };

  const handleScrapeIBS = async () => {
    if (!ibsUrl) {
        toast.warning("Per favore, inserisci un URL di IBS.it.");
        return;
    }
    setIsScraping(true);
    setError('');
    try {
        const response = await scrapeIbsUrl(ibsUrl);
        const scrapedData = response.data;
        
        // In modalità creazione, popola tutti i campi. In modifica, alcuni sono protetti.
        if (isCreating) {
            setFilm(prev => ({ ...prev, ...scrapedData }));
        } else {
            delete scrapedData.titolo; 
            delete scrapedData.barcode;
            delete scrapedData.supporto;
            
            const updatedFilm = { ...film };
            for (const key in scrapedData) {
                if (scrapedData[key] !== null && scrapedData[key] !== undefined) {
                    updatedFilm[key] = scrapedData[key];
                }
            }
            setFilm(updatedFilm);
        }

        toast.success("Dati importati da IBS con successo!");
    } catch (error) {
        setError(error.response?.data?.detail || "Errore durante l'importazione da IBS.it.");
        console.error("Errore scraping IBS:", error);
    } finally {
        setIsScraping(false);
    }
  };

  if (loading) {
    return <Spinner animation="border" />;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!film) {
    return <Alert variant="warning">Film non trovato.</Alert>;
  }

  return (
    <Container>
        <div className="sticky-action-bar">
            <Row>
                <Col>
                    <h2>{isCreating ? 'Nuovo Film' : 'Modifica Film'}</h2>
                </Col>
                <Col className="text-end">
                    <Button variant="info" onClick={handleGenerateWebDescription} disabled={loading || isCreating} className="me-2">
                        {loading ? 'Generazione...' : 'Genera Descrizione Web'}
                    </Button>
                    <Button variant="secondary" onClick={tornaIndietro} className="me-2">
                        Torna alla Lista
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={loading || saving}>
                        {loading || saving ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{saving && <span className="ms-2">Salvataggio…</span>}</> : (isCreating ? 'Salva Nuovo Film' : 'Salva Modifiche')}
                    </Button>
                </Col>
            </Row>
        </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={(e) => e.preventDefault()}>
        {/* Card: CODICI */}
        <Card className="mb-3">
          <Card.Header>CODICI</Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control type="text" name="sku" value={film.sku || ''} onChange={handleSkuChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Barcode</Form.Label>
                  <Form.Control type="text" name="barcode" value={film.barcode || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Barcode IBS {isDvdStoreScraping && <Spinner as="span" animation="border" size="sm" className="ms-1" />}</Form.Label>
                  <InputGroup>
                    <Form.Control type="text" name="barcode_ibs" value={film.barcode_ibs || ''} onChange={handleChange} onBlur={handleBarcodeIbsBlur} />
                    <Button variant="outline-info" onClick={handleImportDvdStore} disabled={isDvdStoreScraping} title="Importa/aggiorna tutti i campi da DVD Store">
                      {isDvdStoreScraping ? <Spinner as="span" animation="border" size="sm" /> : 'DVD Store'}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Importa Dati */}
        <Card className="mb-3">
            <Card.Header>Importa Dati</Card.Header>
            <Card.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Importa da URL IBS</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Incolla l\'URL del prodotto da IBS.it"
                            value={ibsUrl}
                            onChange={(e) => setIbsUrl(e.target.value)}
                        />
                        <Button
                            variant="success"
                            onClick={handleScrapeIBS}
                            disabled={isScraping}
                        >
                            {isScraping ? <Spinner as="span" animation="border" size="sm" /> : 'Importa da IBS'}
                        </Button>
                    </InputGroup>
                </Form.Group>
                <hr />
                <Form.Group className="mb-3">
                    <Form.Label>Cerca su TMDB</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Cerca film per titolo..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                    {showTmdbResults && (
                        <div className="list-group" style={{ zIndex: 1000, position: 'absolute' }}>
                            {tmdbResults.map(movie => (
                                <div key={movie.tmdb_id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                                    {movie.titolo} ({movie.anno}){movie.regista ? ` - ${movie.regista}` : ''}
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleImportTMDB(movie.tmdb_id)}
                                        disabled={isImporting}
                                    >
                                        Importa da TMDB
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Form.Group>
            </Card.Body>
        </Card>

        {/* Card: Informazioni Principali */}
        <Card className="mb-3">
          <Card.Header>Informazioni Principali</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Titolo</Form.Label>
                  <Form.Control type="text" name="titolo" value={film.titolo || ''} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Anno</Form.Label>
                  <Form.Control type="number" name="anno" value={film.anno || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Durata (min)</Form.Label>
                  <Form.Control type="number" name="durata" value={film.durata || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Rating ⭐ 
                    <small className="text-muted"> (TMDB 0-10)</small>
                  </Form.Label>
                  <Form.Control 
                    type="number" 
                    name="rating" 
                    value={film.rating || ''} 
                    onChange={handleChange}
                    step="0.1"
                    min="0"
                    max="10"
                    placeholder="es. 7.5"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Regista</Form.Label>
                  <Form.Control type="text" name="regista" value={film.regista || ''} onChange={handleChange} onBlur={handleListBlur} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Attori</Form.Label>
                  <Form.Control type="text" name="attori" value={film.attori || ''} onChange={handleChange} onBlur={handleListBlur} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Trama</Form.Label>
              <Form.Control as="textarea" rows={3} name="trama" value={film.trama || ''} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Note IBS <span className="text-muted fw-normal small">(differenze rispetto alla versione IBS — appare in descrizione Mirakl)</span></Form.Label>
              <Form.Control as="textarea" rows={2} name="note_ibs" value={film.note_ibs || ''} onChange={handleChange} placeholder="Es. Manca il libretto originale, copertina leggermente rovinata..." />
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Card: Dettagli Prodotto */}
        <Card className="mb-3">
          <Card.Header>Dettagli Prodotto</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Genere</Form.Label>
                  <Form.Select name="genere" value={film.genere || ''} onChange={handleChange}>
                      <option value="">Seleziona un genere...</option>
                      {suggerimenti.generi.map(g => <option key={g} value={g}>{g}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Casa di Produzione</Form.Label>
                  <Form.Control
                    type="text"
                    name="casa_di_produzione"
                    value={film.casa_di_produzione || ''}
                    onChange={handleChange}
                    list="case-produzione-list"
                  />
                  <datalist id="case-produzione-list">
                    {suggerimenti.caseDiProduzione.map(c => <option key={c} value={c} />)}
                  </datalist>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Casa di Distribuzione</Form.Label>
                  <Form.Control
                    type="text"
                    name="casa_di_distribuzione"
                    value={film.casa_di_distribuzione || ''}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoria</Form.Label>
                  <Form.Control type="text" name="categoria" value={film.categoria || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Supporto</Form.Label>
                  <Form.Select name="supporto" value={film.supporto || ''} onChange={handleChange}>
                    <option value="">Seleziona supporto...</option>
                    <option value="4K">4K</option>
                    <option value="DVD">DVD</option>
                    <option value="BLU RAY">BLU RAY</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Quantità <span className="text-muted fw-normal small">(somma delle ubicazioni sotto)</span></Form.Label>
                        <Form.Control type="number" value={quantitaTotale} readOnly disabled />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Condizione</Form.Label>
                        <Form.Select name="condizione" value={film.condizione || ''} onChange={handleChange}>
                            <option value="">Seleziona condizione...</option>
                            <option value="Nuovo">Nuovo</option>
                            <option value="Usato - Ottime Condizioni">Usato - Ottime Condizioni</option>
                            <option value="Usato - Buone Condizioni">Usato - Buone Condizioni</option>
                            <option value="Usato - Discrete Condizioni">Usato - Discrete Condizioni</option>
                            <option value="Usato - Accettabile">Usato - Accettabile</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Prezzi */}
        <Card className="mb-3" id="prezzi">
          <Card.Header>PREZZI</Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo Negozio</Form.Label>
                  <InputGroup>
                    <Form.Control type="number" step="0.01" name="prezzo_negozio" value={film.prezzo_negozio || ''} onChange={handleChange} />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo IBS <span className="text-muted small">(dal prezzo base: +{percentualeIbs}% +{markupFissoIbs}€)</span></Form.Label>
                  <InputGroup>
                    <Form.Control type="number" step="0.01" name="prezzo_ibs" value={film.prezzo_ibs || ''} disabled readOnly />
                    <Button variant="outline-secondary" onClick={handleCalcolaPrezzo} disabled={isCalcoloPrezzo || !film.barcode_ibs} title="Sovrascrivi con il prezzo competitivo da IBS Marketplace (P11)">
                      {isCalcoloPrezzo ? <Spinner as="span" animation="border" size="sm" /> : '💰 P11'}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Costo Acquisto</Form.Label>
                  <Form.Control type="number" step="0.01" name="costo_acquisto" value={film.costo_acquisto || ''} onChange={handleCostoChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo DVD Store</Form.Label>
                  <Form.Control type="number" step="0.01" name="prezzo_dvdstore" value={film.prezzo_dvdstore || ''} onChange={handleChange} placeholder="auto da barcode IBS" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IVA (%)</Form.Label>
                  <Form.Control type="number" step="0.01" name="iva" value={film.iva || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Immagine Film */}
        <Card className="mb-3">
          <Card.Header>Immagine Film</Card.Header>
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <Form.Group controlId="formImmagineFilm" className="mb-3">
                  <Form.Label>Carica/Modifica Immagine</Form.Label>
                  <Form.Control type="file" onChange={handleFileChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                {imagePreviewUrl && (
                  <div className="mt-3">
                    <p>Anteprima Immagine:</p>
                    <img 
                      src={imagePreviewUrl} 
                      alt={film.titolo} 
                      style={{ maxWidth: '200px', height: 'auto' }} 
                      className="img-thumbnail"
                    />
                    <Button variant="danger" size="sm" className="ms-2" onClick={handleDeleteImage}>
                      Elimina Immagine
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Fornitore e Catalogo */}
        <Card className="mb-3">
            <Card.Header>Fornitore e Catalogo</Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Fornitore</Form.Label>
                            <Form.Select name="fornitore_id" value={film.fornitore_id || ''} onChange={handleChange}>
                                <option value="">Seleziona fornitore</option>
                                {fornitori.map(f => <option key={f.id} value={f.id}>{f.denominazione}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" id="ubicazione">
                            <Form.Label>Ubicazioni <span className="text-muted fw-normal small">(una riga per ogni locazione fisica)</span></Form.Label>
                            {ubicazioniRows.map((riga, index) => (
                                <InputGroup className="mb-2" key={index}>
                                    <Form.Control
                                        type="text"
                                        value={riga.ubicazione}
                                        onChange={(e) => handleUbicazioneRowChange(index, 'ubicazione', e.target.value)}
                                        list="ubicazioni-list"
                                        placeholder="Ubicazione"
                                    />
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={riga.quantita}
                                        onChange={(e) => handleUbicazioneRowChange(index, 'quantita', e.target.value)}
                                        style={{ maxWidth: 100 }}
                                        placeholder="Qtà"
                                    />
                                    <Button variant="outline-danger" onClick={() => handleRemoveUbicazioneRow(index)}>&times;</Button>
                                </InputGroup>
                            ))}
                            <datalist id="ubicazioni-list">
                                {suggerimenti.ubicazioni.map(u => <option key={u} value={u} />)}
                            </datalist>
                            <Button variant="outline-secondary" size="sm" onClick={handleAddUbicazioneRow}>+ Aggiungi ubicazione</Button>
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Check type="switch" name="versione_noleggio" label="Versione Noleggio" checked={film.versione_noleggio} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="versione_editoriale" label="Versione Editoriale" checked={film.versione_editoriale || false} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="fuori_catalogo" label="Fuori Catalogo" checked={film.fuori_catalogo} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="da_pubblicare_su_ibs" label="Presente su IBS" checked={film.da_pubblicare_su_ibs || false} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="in_vetrina" label="In Vetrina" checked={film.in_vetrina || false} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="prenotabile" label="Prenotabile (Novità)" checked={film.prenotabile || false} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="riedizione" label="Riedizione / Ed. Speciale" checked={film.riedizione || false} onChange={handleChange} className="d-inline-block" />
                </Form.Group>
                {!isCreating && film.da_pubblicare_su_ibs && (
                    <div className="mb-3">
                        <Button variant="outline-secondary" size="sm" onClick={handleRisincronizzaMirakl} disabled={sincronizzandoMirakl}
                            title="Riallinea l'offerta su Mirakl con i dati già salvati (utile dopo un errore di sincronizzazione)">
                            {sincronizzandoMirakl ? 'Sincronizzazione…' : 'Risincronizza con Mirakl'}
                        </Button>
                    </div>
                )}
                {film.prenotabile && (
                    <Form.Group className="mb-3">
                        <Form.Label>Data di uscita prevista</Form.Label>
                        <Form.Control
                            type="date"
                            name="data_uscita"
                            value={film.data_uscita || ''}
                            onChange={handleChange}
                            style={{ maxWidth: 200 }}
                        />
                        <Form.Text className="text-muted">Se la data è futura il film apparirà nelle "Prossime uscite" dell'ecommerce.</Form.Text>
                    </Form.Group>
                )}
                {film.riedizione && (
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo uscita / Edizione</Form.Label>
                        <Form.Control
                            type="text"
                            name="tipo_uscita"
                            value={film.tipo_uscita || ''}
                            onChange={handleChange}
                            placeholder="Es. Edizione 4K, Director's Cut, Edizione anniversario 40°..."
                            style={{ maxWidth: 400 }}
                            list="tipi-uscita-list"
                        />
                        <datalist id="tipi-uscita-list">
                            {suggerimenti.tipiUscita.map(t => <option key={t} value={t} />)}
                        </datalist>
                        <Form.Text className="text-muted">Descrizione breve del tipo di riedizione, visibile nelle Novità dell'ecommerce.</Form.Text>
                    </Form.Group>
                )}
                {film.versione_editoriale && (
                    <div className="mb-3 p-3 border rounded bg-light">
                        <p className="mb-2 text-muted small fw-bold">📦 VERSIONE EDITORIALE — Barcode fittizio</p>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleGeneraBarcode}
                            disabled={isGeneratingBarcode}
                        >
                            {isGeneratingBarcode ? 'Generazione...' : '⚙️ Genera Barcode Editoriale'}
                        </Button>
                        {film.barcode && film.barcode.startsWith('ED') && (
                            <span className="ms-3 font-monospace text-success">{film.barcode}</span>
                        )}
                        <p className="mt-2 mb-0 text-muted small">
                            Il barcode (e lo SKU) vengono generati automaticamente al salvataggio se non già presenti.
                        </p>
                    </div>
                )}
            </Card.Body>
        </Card>

        <div className="text-end mb-4">
            <Button variant="secondary" onClick={tornaIndietro} className="me-2">
                Torna alla Lista
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={loading || saving}>
                {loading || saving ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{saving && <span className="ms-2">Salvataggio…</span>}</> : (isCreating ? 'Salva Nuovo Film' : 'Salva Modifiche')}
            </Button>
        </div>

      </Form>
    </Container>
  );
};

export default FilmEdit;